import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../services/api";
import { toast } from "react-toastify";
import { useSocket } from "./SocketContext";
import "./SystemHubBilling.css";
import BillingMonitorModal from './BillingMonitorModal';

import {
  FaSearch,
  FaSyncAlt,
  FaBuilding,
  FaUserTie,
  FaReceipt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBan,
  FaBolt,
  FaRegCopy,
  FaQrcode,
  FaFileInvoiceDollar,
  FaPaperPlane,
  FaCog,
  FaClock,
  FaArrowRight,
  FaTimes,
  FaSave,
  FaWallet,
  FaEnvelope,
  FaPhone,
  FaTrash,
  FaCalendarAlt,
} from "react-icons/fa";

const moneyBRL = (cents = 0) =>
  (Number(cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const isoDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const competenceNow = () => {
  const dt = new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}`;
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

// Opções do portão de início de cobrança — mesmo vocabulário usado na
// Central de Conversões (30/60/90 dias a partir de hoje, ou data manual).
const BILLING_START_OPTIONS = [
  { key: "30", label: "D+30" },
  { key: "60", label: "D+60" },
  { key: "90", label: "D+90" },
  { key: "custom", label: "Data personalizada" },
];

const todayYMD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

function Badge({ variant = "neutral", children, title }) {
  return (
    <span className={`shb-badge shb-badge--${variant}`} title={title}>
      {children}
    </span>
  );
}

function Pill({ icon, label, value, variant = "neutral" }) {
  return (
    <div className={`shb-kpi shb-kpi--${variant}`}>
      <div className="shb-kpi__icon">{icon}</div>
      <div className="shb-kpi__meta">
        <div className="shb-kpi__label">{label}</div>
        <div className="shb-kpi__value">{value}</div>
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="shb-skeleton-row">
      <div className="shb-skeleton shb-skeleton--lg" />
      <div className="shb-skeleton shb-skeleton--md" />
      <div className="shb-skeleton shb-skeleton--md" />
      <div className="shb-skeleton shb-skeleton--sm" />
      <div className="shb-skeleton shb-skeleton--sm" />
      <div className="shb-skeleton shb-skeleton--md" />
    </div>
  );
}

function Modal({ open, title, children, onClose, width = 640 }) {
  if (!open) return null;
  return (
    <div className="shb-modal-backdrop" onMouseDown={onClose}>
      <div
        className="shb-modal"
        style={{ width }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="shb-modal__header">
          <h3>{title}</h3>
          <button className="shb-icon-btn" onClick={onClose} title="Fechar">
            <FaTimes />
          </button>
        </div>
        <div className="shb-modal__body">{children}</div>
      </div>
    </div>
  );
}

function Drawer({ open, title, subtitle, onClose, children }) {
  if (!open) return null;

  return (
    <div className="shb-drawer open">
      <div className="shb-drawer__overlay" onMouseDown={onClose} />
      <div
        className="shb-drawer__panel"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="shb-drawer__header">
          <div className="shb-drawer__titleblock">
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button className="shb-icon-btn" onClick={onClose} title="Fechar painel">
            <FaTimes />
          </button>
        </div>
        <div className="shb-drawer__content">{children}</div>
      </div>
    </div>
  );
}

function Tabs({ tabs, active, setActive }) {
  return (
    <div className="shb-tabs">
      {tabs.map((t) => (
        <button
          key={t.key}
          className={`shb-tab ${active === t.key ? "active" : ""}`}
          onClick={() => setActive(t.key)}
        >
          {t.icon ? <span className="shb-tab__icon">{t.icon}</span> : null}
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── Card do responsável financeiro ────────────────────────────────────────────
function BillingContactCard({ contact }) {
  if (!contact) {
    return (
      <div className="shb-info">
        <FaExclamationTriangle style={{ color: "#f59e0b", flexShrink: 0 }} />
        <div>
          <b>Nenhum responsável financeiro definido</b>
          <p>
            Vá em <b>Clientes → Gerenciar → Visão Geral &amp; Contatos</b> e
            marque um contato como <em>"Responsável Financeiro"</em> para que ele
            receba boletos, PIX e notificações de cobrança automaticamente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        padding: "16px",
        background: "var(--shb-surface, #f8fafc)",
        border: "1.5px solid var(--shb-border, #e2e8f0)",
        borderRadius: "10px",
      }}
    >
      {/* Nome + tag */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div
          style={{
            width: "38px",
            height: "38px",
            borderRadius: "50%",
            background: "var(--shb-brand, #4f46e5)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: "1rem",
            flexShrink: 0,
          }}
        >
          {(contact.nome || "?").charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: "0.97rem", lineHeight: 1.2 }}>
            {contact.nome}
          </div>
          <Badge variant="brand">{contact.cargo_tag || "Financeiro"}</Badge>
        </div>
      </div>

      {/* Email */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.88rem", color: "var(--shb-text-muted, #64748b)" }}>
        <FaEnvelope style={{ flexShrink: 0 }} />
        <span>{contact.email || <em>Sem e-mail</em>}</span>
      </div>

      {/* Telefone */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.88rem", color: "var(--shb-text-muted, #64748b)" }}>
        <FaPhone style={{ flexShrink: 0 }} />
        <span>{contact.telefone || <em>Sem telefone</em>}</span>
      </div>

      {/* CPF */}
      {contact.documento && (
        <div style={{ fontSize: "0.82rem", color: "var(--shb-text-muted, #64748b)" }}>
          CPF: {contact.documento}
        </div>
      )}

      <div
        style={{
          marginTop: "4px",
          padding: "8px 10px",
          background: "#fffbeb",
          border: "1px solid #fde68a",
          borderRadius: "6px",
          fontSize: "0.78rem",
          color: "#92400e",
        }}
      >
        Para alterar este responsável, acesse{" "}
        <b>SystemHub → Clientes → Gerenciar → Visão Geral &amp; Contatos</b>.
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────

export default function SystemHubBilling() {
  const socket = useSocket();

  // ===========================
  // Listagem
  // ===========================
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const limit = 12;

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [billingFilter, setBillingFilter] = useState(""); // '' | 'on' | 'off'
  const [disabledBillingCount, setDisabledBillingCount] = useState(0);
  const [monitorOpen, setMonitorOpen] = useState(false);

  // ===========================
  // Drawer (detalhe)
  // ===========================
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [cliente, setCliente] = useState(null);
  const [settings, setSettings] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [events, setEvents] = useState([]);

  // ── responsável financeiro vindo de clientes_contatos ──
  const [billingContact, setBillingContact] = useState(null);

  // ===========================
  // Modals
  // ===========================
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);

  const [invoiceDraft, setInvoiceDraft] = useState(() => ({
    competencia: competenceNow(),
    amountBRL: "199,90",
    due_date: isoDate(new Date()),
  }));

  const [notifyDraft, setNotifyDraft] = useState(() => ({
    invoice_id: null,
    type: "overdue",
    channels: ["email"],
  }));

  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingBilling, setTogglingBilling] = useState(false);

  // ── Ajuste de início de cobrança (billing_start_date), aba Régua ──
  const [editBillingStartOpen, setEditBillingStartOpen] = useState(false);
  const [billingStartMode, setBillingStartMode] = useState("30");
  const [billingStartCustomDate, setBillingStartCustomDate] = useState("");
  const [savingBillingStart, setSavingBillingStart] = useState(false);

  useEffect(() => {
    if (editBillingStartOpen) {
      setBillingStartMode("30");
      setBillingStartCustomDate("");
    }
  }, [editBillingStartOpen]);

  // Trava de cobrança automática: default ligada (1) quando ainda não configurada.
  const billingEnabled = (settings?.billing_enabled ?? 1) === 1;

  // Portão de início de cobrança: sem data (ainda não configurado) ou data no
  // futuro = bloqueado. Mesma regra do backend (assertBillingStarted).
  const billingStartDateIso = settings?.billing_start_date ? isoDate(settings.billing_start_date) : null;
  const billingStarted = billingStartDateIso ? billingStartDateIso <= todayYMD() : false;

  // ===========================
  // KPI Computed
  // ===========================
  const kpis = useMemo(() => {
    const totalClients = total;
    const suspensos = items.filter((i) => i.status === "suspenso").length;
    const inadimplentes = items.filter((i) => Number(i.days_overdue || 0) > 0).length;

    const aberto = items.reduce((acc, i) => {
      if (["open", "overdue"].includes(i.invoice_status)) {
        return acc + Number(i.amount_cents || 0);
      }
      return acc;
    }, 0);

    return { totalClients, suspensos, inadimplentes, aberto };
  }, [items, total]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  // ===========================
  // Fetch list
  // ===========================
  const fetchClients = useCallback(
    async ({ soft = false } = {}) => {
      if (!soft) setLoading(true);
      setRefreshing(true);
      try {
        const { data } = await api.get("/api/system-hub/billing/clients", {
          params: {
            page,
            limit,
            q: q || undefined,
            status: statusFilter || undefined,
            billing: billingFilter || undefined,
          },
        });
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTotal(Number(data?.total || 0));
        setDisabledBillingCount(Number(data?.disabledBillingCount || 0));
      } catch {
        toast.error("Falha ao carregar clientes de cobrança.");
        setItems([]);
        setTotal(0);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, limit, q, statusFilter, billingFilter]
  );

  // ===========================
  // Fetch detail
  // ===========================
  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      // Roda billing + contatos em paralelo (mais rápido que em série)
      const [detailRes, contactsRes] = await Promise.allSettled([
        api.get(`/api/system-hub/billing/clients/${id}`),
        api.get(`/api/system-hub/clientes/${id}/contatos`),
      ]);

      if (detailRes.status === "fulfilled") {
        const { data } = detailRes.value;
        setCliente(data?.cliente || null);
        setSettings(data?.settings || null);
        setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
        setEvents(Array.isArray(data?.events) ? data.events : []);
      } else {
        throw detailRes.reason;
      }

      if (contactsRes.status === "fulfilled") {
        const contacts = contactsRes.value.data;
        const financeiro = Array.isArray(contacts)
          ? contacts.find((c) => c.is_billing)
          : null;
        setBillingContact(financeiro || null);
      } else {
        setBillingContact(null);
      }
    } catch {
      toast.error("Falha ao carregar detalhes do cliente.");
      setCliente(null);
      setSettings(null);
      setInvoices([]);
      setEvents([]);
      setBillingContact(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ===========================
  // Socket realtime refresh
  // ===========================
  useEffect(() => {
    if (!socket) return;

    const handler = () => {
      fetchClients({ soft: true });
      if (drawerOpen && selectedId) fetchDetail(selectedId);
    };

    socket.on("system_hub_billing_update", handler);
    return () => socket.off("system_hub_billing_update", handler);
  }, [socket, fetchClients, fetchDetail, drawerOpen, selectedId]);

  // ===========================
  // Init list
  // ===========================
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // ===========================
  // Open / close detail
  // ===========================
  const openDetail = useCallback(
    async (id) => {
      setSelectedId(id);
      setDrawerOpen(true);
      await fetchDetail(id);
    },
    [fetchDetail]
  );

  const closeDetail = useCallback(() => {
    setDrawerOpen(false);
    setSelectedId(null);
    setCliente(null);
    setSettings(null);
    setInvoices([]);
    setEvents([]);
    setBillingContact(null);
  }, []);

  // ===========================
  // Actions: Suspend / Unsuspend
  // ===========================
  const suspendClient = async () => {
    if (!cliente?.id) return;
    try {
      await api.post(`/api/system-hub/billing/clients/${cliente.id}/suspend`, {
        motivo: "inadimplencia",
      });
      toast.success("Cliente suspenso com sucesso.");
      fetchDetail(cliente.id);
      fetchClients({ soft: true });
    } catch {
      toast.error("Falha ao suspender cliente.");
    }
  };

  const unsuspendClient = async () => {
    if (!cliente?.id) return;
    try {
      await api.post(`/api/system-hub/billing/clients/${cliente.id}/unsuspend`);
      toast.success("Cliente reativado com sucesso.");
      fetchDetail(cliente.id);
      fetchClients({ soft: true });
    } catch {
      toast.error("Falha ao reativar cliente.");
    }
  };

  // ===========================
  // Trava de cobrança automática (kill switch por cliente)
  // Ação instantânea, separada do "Salvar" da régua: não depende de você
  // preencher o resto do formulário para ligar/desligar.
  // ===========================
  const toggleBillingEnabled = async (nextValue) => {
    if (!cliente?.id) return;
    setTogglingBilling(true);
    try {
      await api.post(
        `/api/system-hub/billing/clients/${cliente.id}/billing-toggle`,
        { enabled: nextValue }
      );
      toast.success(
        nextValue
          ? "Cobrança automática ativada para este cliente."
          : "Cobrança automática pausada para este cliente."
      );
      setSettings((s) => ({ ...(s || {}), billing_enabled: nextValue ? 1 : 0 }));
      fetchClients({ soft: true });
    } catch (e) {
      toast.error(
        e?.response?.data?.error || "Falha ao alternar cobrança automática."
      );
    } finally {
      setTogglingBilling(false);
    }
  };

  // ===========================
  // Settings: save
  // ===========================
  const updateSettingsField = (key, value) => {
    setSettings((s) => ({ ...(s || {}), [key]: value }));
  };

  const saveSettings = async () => {
    if (!cliente?.id) return;
    setSavingSettings(true);
    try {
      const payload = {
  responsavel_usuario_id: settings?.responsavel_usuario_id || null,
  due_day: Number(settings?.due_day ?? 10),
  pre_due_days: Number(settings?.pre_due_days ?? 3),
  notify_every_days: Number(settings?.notify_every_days ?? 2),
  block_after_days: Number(settings?.block_after_days ?? 7),
  auto_suspend: !!settings?.auto_suspend,
  auto_unsuspend: !!settings?.auto_unsuspend,
  notify_after_suspend: settings?.notify_after_suspend !== undefined ? !!settings?.notify_after_suspend : true,
  send_email: settings?.send_email !== undefined ? !!settings?.send_email : true,
  send_sms: !!settings?.send_sms,
};

      await api.put(
        `/api/system-hub/billing/clients/${cliente.id}/settings`,
        payload
      );
      toast.success("Régua e responsável atualizados.");
      fetchDetail(cliente.id);
      fetchClients({ soft: true });
    } catch {
      toast.error("Falha ao salvar configurações.");
    } finally {
      setSavingSettings(false);
    }
  };

  // ===========================
  // Billing start: ajustar/corrigir portão de início de cobrança
  // ===========================
  const saveBillingStart = async () => {
    if (!cliente?.id) return;
    if (billingStartMode === "custom" && !billingStartCustomDate) {
      toast.warn("Selecione a data personalizada para o início da cobrança.");
      return;
    }
    setSavingBillingStart(true);
    try {
      const { data } = await api.post(
        `/api/system-hub/billing/clients/${cliente.id}/billing-start`,
        {
          billing_start_mode: billingStartMode,
          billing_start_date_custom:
            billingStartMode === "custom" ? billingStartCustomDate : undefined,
        }
      );
      toast.success(
        `Início da cobrança atualizado para ${isoDate(data.billing_start_date)}.`
      );
      setEditBillingStartOpen(false);
      fetchDetail(cliente.id);
      fetchClients({ soft: true });
    } catch (e) {
      toast.error(
        e?.response?.data?.error || "Falha ao atualizar o início da cobrança."
      );
    } finally {
      setSavingBillingStart(false);
    }
  };

  // ===========================
  // Invoice: create
  // ===========================
  const openCreateInvoice = () => {
    const baseCompetencia = competenceNow();
    const due = new Date();
    const dueDay = clamp(Number(settings?.due_day ?? 10), 1, 28);
    due.setDate(dueDay);

    setInvoiceDraft((prev) => ({
      competencia: baseCompetencia,
      amountBRL: prev.amountBRL || "199,90",
      due_date: isoDate(due),
    }));

    setCreateInvoiceOpen(true);
  };

  const parseBRLToCents = (s) => {
    const normalized = String(s || "")
      .replace(/\./g, "")
      .replace(",", ".")
      .replace(/[^\d.]/g, "");
    const v = Number(normalized);
    if (!Number.isFinite(v)) return 0;
    return Math.round(v * 100);
  };

  const createInvoice = async () => {
    if (!cliente?.id) return;

    const amount_cents = parseBRLToCents(invoiceDraft.amountBRL);
    if (!invoiceDraft.competencia || invoiceDraft.competencia.length !== 7) {
      toast.warn("Competência inválida. Use YYYY-MM (ex: 2026-04).");
      return;
    }
    if (!amount_cents || amount_cents <= 0) {
      toast.warn("Valor inválido.");
      return;
    }
    if (!invoiceDraft.due_date) {
      toast.warn("Vencimento obrigatório.");
      return;
    }

    try {
      await api.post("/api/system-hub/billing/invoices", {
        cliente_id: cliente.id,
        competencia: invoiceDraft.competencia,
        amount_cents,
        due_date: invoiceDraft.due_date,
      });
      toast.success("Fatura criada com sucesso.");
      setCreateInvoiceOpen(false);
      fetchDetail(cliente.id);
      fetchClients({ soft: true });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Falha ao criar fatura.");
    }
  };

  // ===========================
  // Invoice: charge
  // ===========================
  const generateCharge = async (invoiceId, method) => {
    try {
      await api.post(`/api/system-hub/billing/invoices/${invoiceId}/charge`, {
        method,
      });
      toast.success(`Cobrança ${method.toUpperCase()} gerada.`);
      fetchDetail(cliente.id);
      fetchClients({ soft: true });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Falha ao gerar cobrança.");
    }
  };

  // ===========================
  // Invoice: delete
  // ===========================
  const deleteInvoice = async (inv) => {
    const ok = window.confirm(
      `Excluir a fatura #${inv.id} (${inv.competencia})?\n\n` +
      (inv.asaas_charge_id
        ? "A cobrança vinculada será cancelada no Asaas.\n"
        : "") +
      "Esta ação não pode ser desfeita."
    );
    if (!ok) return;
    try {
      await api.delete(`/api/system-hub/billing/invoices/${inv.id}`);
      toast.success("Fatura excluída.");
      fetchDetail(cliente.id);
      fetchClients({ soft: true });
    } catch (e) {
      toast.error(e?.response?.data?.error || "Falha ao excluir fatura.");
    }
  };

  // ===========================
  // Notify
  // ===========================
  const openNotify = (invoiceId) => {
    setNotifyDraft({ invoice_id: invoiceId, type: "overdue", channels: ["email"] });
    setNotifyOpen(true);
  };

  const toggleChannel = (ch) => {
    setNotifyDraft((d) => {
      const set = new Set(d.channels || []);
      if (set.has(ch)) set.delete(ch);
      else set.add(ch);
      return { ...d, channels: Array.from(set) };
    });
  };

  const sendNotify = async () => {
    try {
      await api.post(
        `/api/system-hub/billing/invoices/${notifyDraft.invoice_id}/notify`,
        { type: notifyDraft.type, channels: notifyDraft.channels }
      );
      toast.success("Comunicação enviada.");
      setNotifyOpen(false);
      fetchDetail(cliente.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Falha ao enviar comunicação.");
    }
  };

  // ===========================
  // Copy helpers
  // ===========================
  const copyText = async (txt) => {
    try {
      await navigator.clipboard.writeText(String(txt || ""));
      toast.success("Copiado!");
    } catch {
      toast.error("Não consegui copiar.");
    }
  };

  // ===========================
  // Drawer tabs
  // ===========================
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    if (drawerOpen) setTab("overview");
  }, [drawerOpen]);

  // ===========================
  // Render helpers
  // ===========================
  const statusBadge = (st) => {
    if (st === "ativo") return <Badge variant="success">ATIVO</Badge>;
    if (st === "suspenso") return <Badge variant="danger">SUSPENSO</Badge>;
    return <Badge variant="neutral">{String(st || "—").toUpperCase()}</Badge>;
  };

  const invoiceBadge = (st) => {
    if (st === "paid") return <Badge variant="success">PAGO</Badge>;
    if (st === "overdue") return <Badge variant="danger">VENCIDO</Badge>;
    if (st === "open") return <Badge variant="warning">EM ABERTO</Badge>;
    if (st === "canceled") return <Badge variant="neutral">CANCELADO</Badge>;
    if (st === "refunded") return <Badge variant="neutral">ESTORNADO</Badge>;
    return <Badge variant="neutral">{String(st || "—").toUpperCase()}</Badge>;
  };

  // Verdadeiro se o portão de início de cobrança ainda não abriu (ou nunca
  // foi configurado) para este cliente — mesma regra do backend
  // (assertBillingStarted): sem data, ou data no futuro, bloqueia.
  const billingNotStarted = (billingStartDate) => {
    if (!billingStartDate) return true;
    return isoDate(billingStartDate) > todayYMD();
  };

  // ===========================
  // UI
  // ===========================
  return (
    <div className="shb-wrap">
      {/* Header */}
      <div className="shb-header">
        <div className="shb-header__title">
          <h1>
            <FaReceipt /> Cobrança &amp; Assinaturas
          </h1>
          <p>
            Controle multi-tenant: inadimplência, régua de comunicação, boleto/PIX
            (Asaas) e suspensão automática.
          </p>
        </div>

        <div className="shb-header__actions">
          <button
            className="shb-btn shb-btn--ghost"
            onClick={() => setMonitorOpen(true)}
            title="Abrir monitor de faturas, comunicações e webhooks"
          >
            <FaFileInvoiceDollar /> Monitor
          </button>
          <button
            className="shb-btn shb-btn--ghost"
            onClick={() => fetchClients({ soft: true })}
            disabled={refreshing}
            title="Recarregar"
          >
            <FaSyncAlt className={refreshing ? "spin" : ""} /> Atualizar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="shb-kpis">
        <Pill icon={<FaBuilding />} label="Clientes" value={String(kpis.totalClients)} />
        <Pill
          icon={<FaBan />}
          label="Suspensos"
          value={String(kpis.suspensos)}
          variant="danger"
        />
        <Pill
          icon={<FaExclamationTriangle />}
          label="Inadimplentes"
          value={String(kpis.inadimplentes)}
          variant="warning"
        />
        <Pill
          icon={<FaWallet />}
          label="Em aberto (visível)"
          value={moneyBRL(kpis.aberto)}
          variant="brand"
        />
        <Pill
          icon={<FaBolt />}
          label="Cobrança pausada"
          value={String(disabledBillingCount)}
          variant={disabledBillingCount > 0 ? "warning" : "neutral"}
        />
      </div>

      {/* Toolbar */}
      <div className="shb-toolbar">
        <div className="shb-search">
          <FaSearch />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar cliente… (nome)"
          />
        </div>

        <div className="shb-filters">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Status: todos</option>
            <option value="ativo">Ativos</option>
            <option value="suspenso">Suspensos</option>
          </select>

          <select
            value={billingFilter}
            onChange={(e) => {
              setBillingFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Cobrança automática: todas</option>
            <option value="on">Cobrança: ativa</option>
            <option value="off">Cobrança: pausada</option>
          </select>

          <button
            className="shb-btn shb-btn--primary"
            onClick={() => fetchClients({ soft: true })}
            disabled={refreshing}
          >
            <FaBolt /> Aplicar
          </button>
        </div>
      </div>

      {/* List */}
      <div className="shb-card">
        <div className="shb-card__header">
          <h2>Clientes</h2>
          <span className="shb-card__hint">
            Clique em um cliente para abrir o painel lateral com faturas, régua e ações.
          </span>
        </div>

        <div className="shb-table">
          <div className="shb-table__head">
            <div>Cliente</div>
            <div>Status</div>
            <div>Última fatura</div>
            <div>Vencimento</div>
            <div>Atraso</div>
            <div>Valor</div>
            <div>Ações</div>
          </div>

          <div className="shb-table__body">
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : items.length === 0 ? (
              <div className="shb-empty">
                <FaBuilding size={34} />
                <h3>Nenhum cliente encontrado</h3>
                <p>Altere os filtros ou tente outra busca.</p>
              </div>
            ) : (
              items.map((c) => (
                <div
                  className="shb-table__row"
                  key={c.id}
                  onClick={() => openDetail(c.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openDetail(c.id);
                  }}
                >
                  <div className="shb-cell shb-client">
                    <div className="shb-client__avatar">
                      {(c.nome_empresa || "C").charAt(0).toUpperCase()}
                    </div>
                    <div className="shb-client__meta">
                      <strong>
                        {c.nome_empresa}
                        {Number(c.billing_enabled ?? 1) === 0 && (
                          <Badge variant="muted" title="Cobrança automática pausada para este cliente">
                            Cobrança OFF
                          </Badge>
                        )}
                        {billingNotStarted(c.billing_start_date) && (
                          <Badge
                            variant="pending"
                            title={
                              c.billing_start_date
                                ? `Cobrança liberada a partir de ${isoDate(c.billing_start_date)}`
                                : "Início da cobrança ainda não foi definido para este cliente"
                            }
                          >
                            Aguardando início
                          </Badge>
                        )}
                      </strong>
                      <span className="muted">ID {c.id}</span>
                    </div>
                  </div>

                  <div className="shb-cell">{statusBadge(c.status)}</div>

                  <div className="shb-cell">
                    {c.invoice_status ? invoiceBadge(c.invoice_status) : <Badge>—</Badge>}
                  </div>

                  <div className="shb-cell">{c.due_date ? isoDate(c.due_date) : "—"}</div>

                  <div className="shb-cell">
                    {Number(c.days_overdue || 0) > 0 ? (
                      <Badge variant="danger">{c.days_overdue}d</Badge>
                    ) : (
                      <Badge variant="success">OK</Badge>
                    )}
                  </div>

                  <div className="shb-cell">
                    {c.amount_cents ? moneyBRL(c.amount_cents) : "—"}
                  </div>

                  <div
                    className="shb-cell shb-row-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="shb-btn-mini"
                      onClick={() => openDetail(c.id)}
                      title="Abrir detalhes"
                    >
                      <FaArrowRight />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination */}
        <div className="shb-pagination">
          <button
            className="shb-btn shb-btn--ghost"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Voltar
          </button>

          <div className="shb-pagination__info">
            Página <b>{page}</b> de <b>{totalPages}</b> • Total <b>{total}</b>
          </div>

          <button
            className="shb-btn shb-btn--ghost"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Próxima
          </button>
        </div>
      </div>

      {/* Drawer Detail */}
      <Drawer
        open={drawerOpen}
        title={cliente?.nome_empresa || "Cliente"}
        subtitle={
          cliente
            ? `Tenant ID ${cliente.id} • Status: ${String(cliente.status || "").toUpperCase()}${
                !billingEnabled ? " • Cobrança automática PAUSADA" : ""
              }`
            : "Carregando…"
        }
        onClose={closeDetail}
      >
        <Tabs
          active={tab}
          setActive={setTab}
          tabs={[
            { key: "overview", label: "Visão Geral", icon: <FaBuilding /> },
            { key: "invoices", label: "Faturas", icon: <FaFileInvoiceDollar /> },
            { key: "rules", label: "Régua & Responsável", icon: <FaCog /> },
            { key: "history", label: "Histórico", icon: <FaClock /> },
          ]}
        />

        {detailLoading ? (
          <div className="shb-drawer-loading">
            <div className="shb-skeleton shb-skeleton--xl" />
            <div className="shb-skeleton shb-skeleton--xl" />
            <div className="shb-skeleton shb-skeleton--xl" />
          </div>
        ) : (
          <>
            {/* ── OVERVIEW ─────────────────────────────────────────── */}
            {tab === "overview" && cliente && (
              <div className="shb-pane">
                <div className="shb-grid2">
                  <div className="shb-panel">
                    <h3>Status do Cliente</h3>
                    <div className="shb-panel__row">
                      <span className="muted">Situação</span>
                      <span>{statusBadge(cliente.status)}</span>
                    </div>
                    <div className="shb-panel__row">
                      <span className="muted">Motivo</span>
                      <span>{cliente.suspensao_motivo || "—"}</span>
                    </div>
                    <div className="shb-panel__row">
                      <span className="muted">Suspenso em</span>
                      <span>
                        {cliente.suspenso_em ? isoDate(cliente.suspenso_em) : "—"}
                      </span>
                    </div>

                    <div className="shb-panel__actions">
                      {cliente.status === "ativo" ? (
                        <button className="shb-btn shb-btn--danger" onClick={suspendClient}>
                          <FaBan /> Suspender por inadimplência
                        </button>
                      ) : (
                        <button className="shb-btn shb-btn--success" onClick={unsuspendClient}>
                          <FaCheckCircle /> Reativar cliente
                        </button>
                      )}
                      <p className="shb-panel__note">
                        Mensagem padrão para usuários suspensos:{" "}
                        <b>
                          Sistema suspenso temporariamente. Procure a Gestão da
                          Conta/Suporte
                        </b>
                      </p>
                    </div>
                  </div>

                  <div className="shb-panel">
                    <h3>Atalho Operacional</h3>
                    <p className="muted">
                      Aqui você faz o "triângulo da cobrança": cria fatura → gera
                      cobrança → notifica.
                    </p>

                    <div className="shb-quick-actions">
                      <button
                        className="shb-btn shb-btn--primary"
                        onClick={openCreateInvoice}
                        disabled={!billingStarted}
                        title={
                          billingStarted
                            ? undefined
                            : billingStartDateIso
                            ? `Cobrança liberada apenas a partir de ${billingStartDateIso}`
                            : "Defina o início da cobrança na aba Régua & Responsável"
                        }
                      >
                        <FaFileInvoiceDollar /> Criar nova fatura
                      </button>

                      <button
                        className="shb-btn shb-btn--ghost"
                        onClick={() => setTab("invoices")}
                      >
                        <FaArrowRight /> Ir para Faturas
                      </button>

                      <button
                        className="shb-btn shb-btn--ghost"
                        onClick={() => setMonitorOpen(true)}
                      >
                        <FaClock /> Ver no Monitor
                      </button>
                    </div>

                    <div className="shb-mini-kpis">
                      <div className="shb-mini-kpi">
                        <span className="muted">Faturas (últimas)</span>
                        <b>{invoices.length}</b>
                      </div>
                      <div className="shb-mini-kpi">
                        <span className="muted">Eventos</span>
                        <b>{events.length}</b>
                      </div>
                    </div>

                    {!billingStarted && (
                      <div className="shb-info" style={{ marginTop: "10px" }}>
                        <FaCalendarAlt />
                        <div>
                          <b>Cobrança bloqueada</b>
                          <p>
                            {billingStartDateIso
                              ? `Nenhuma fatura pode ser gerada até ${billingStartDateIso}. Ajuste em Régua & Responsável.`
                              : "Início da cobrança ainda não foi definido. Configure em Régua & Responsável."}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── INVOICES ─────────────────────────────────────────── */}
            {tab === "invoices" && (
              <div className="shb-pane">
                <div className="shb-pane__header">
                  <h3>Faturas</h3>
                  <button
                    className="shb-btn shb-btn--primary"
                    onClick={openCreateInvoice}
                    disabled={!billingStarted}
                    title={
                      billingStarted
                        ? undefined
                        : billingStartDateIso
                        ? `Cobrança liberada apenas a partir de ${billingStartDateIso}`
                        : "Defina o início da cobrança na aba Régua & Responsável"
                    }
                  >
                    <FaFileInvoiceDollar /> Nova fatura
                  </button>
                </div>

                {!billingStarted && (
                  <div className="shb-info" style={{ marginBottom: "12px" }}>
                    <FaCalendarAlt />
                    <div>
                      <b>Cobrança bloqueada</b>
                      <p>
                        {billingStartDateIso
                          ? `Criação de fatura e geração de boleto/PIX liberadas apenas a partir de ${billingStartDateIso}.`
                          : "Início da cobrança ainda não foi definido. Configure em Régua & Responsável."}
                      </p>
                    </div>
                  </div>
                )}

                {invoices.length === 0 ? (
                  <div className="shb-empty shb-empty--small">
                    <FaReceipt size={28} />
                    <h4>Nenhuma fatura cadastrada</h4>
                    <p>Crie uma fatura para começar a cobrança.</p>
                  </div>
                ) : (
                  <div className="shb-invoice-list">
                    {invoices.map((inv) => (
                      <div className="shb-invoice-card" key={inv.id}>
                        <div className="shb-invoice-card__top">
                          <div>
                            <div className="shb-invoice-card__title">
                              <b>#{inv.id}</b> • Competência <b>{inv.competencia}</b>
                            </div>
                            <div className="shb-invoice-card__meta">
                              <span>{invoiceBadge(inv.status)}</span>
                              <span className="muted">Venc.: {isoDate(inv.due_date)}</span>
                              <span className="muted">Valor: {moneyBRL(inv.amount_cents)}</span>
                            </div>
                          </div>

                          <div className="shb-invoice-card__actions">
                            <button
                              className="shb-btn-mini"
                              onClick={() => generateCharge(inv.id, "boleto")}
                              disabled={!billingStarted}
                              title={
                                billingStarted
                                  ? "Gerar BOLETO"
                                  : `Cobrança bloqueada até ${billingStartDateIso || "definir início"}`
                              }
                            >
                              <FaFileInvoiceDollar />
                            </button>
                            <button
                              className="shb-btn-mini"
                              onClick={() => generateCharge(inv.id, "pix")}
                              disabled={!billingStarted}
                              title={
                                billingStarted
                                  ? "Gerar PIX"
                                  : `Cobrança bloqueada até ${billingStartDateIso || "definir início"}`
                              }
                            >
                              <FaQrcode />
                            </button>
                            <button
                              className="shb-btn-mini"
                              onClick={() => openNotify(inv.id)}
                              title="Enviar comunicação"
                            >
                              <FaPaperPlane />
                            </button>
                            {inv.status !== "paid" && (
                              <button
                                className="shb-btn-mini shb-btn-mini--danger"
                                onClick={() => deleteInvoice(inv)}
                                title="Excluir fatura"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="shb-invoice-card__details">
                          <div className="shb-paybox">
                            <div className="shb-paybox__head">
                              <span>
                                <b>BOLETO</b>
                              </span>
                              {inv.boleto_line ? (
                                <button
                                  className="shb-link"
                                  onClick={() => copyText(inv.boleto_line)}
                                >
                                  <FaRegCopy /> copiar linha
                                </button>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </div>
                            <div className="shb-paybox__body">
                              {inv.boleto_url ? (
                                <a
                                  className="shb-link"
                                  href={inv.boleto_url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Abrir boleto (PDF/URL)
                                </a>
                              ) : (
                                <span className="muted">Sem URL de boleto</span>
                              )}
                              <div className="shb-mono">{inv.boleto_line || "—"}</div>
                            </div>
                          </div>

                          <div className="shb-paybox">
                            <div className="shb-paybox__head">
                              <span>
                                <b>PIX</b>
                              </span>
                              {inv.pix_copia_cola ? (
                                <button
                                  className="shb-link"
                                  onClick={() => copyText(inv.pix_copia_cola)}
                                >
                                  <FaRegCopy /> copiar pix
                                </button>
                              ) : (
                                <span className="muted">—</span>
                              )}
                            </div>
                            <div className="shb-paybox__body">
                              {inv.pix_qr_code ? (
                                <div className="shb-qr">
                                  <img
                                    alt="QR Code PIX"
                                    src={`data:image/png;base64,${inv.pix_qr_code}`}
                                  />
                                  <div className="shb-qr__meta">
                                    <span className="muted">Expira:</span>{" "}
                                    <b>
                                      {inv.pix_expiration
                                        ? isoDate(inv.pix_expiration)
                                        : "—"}
                                    </b>
                                  </div>
                                </div>
                              ) : (
                                <span className="muted">Sem QR Code disponível</span>
                              )}
                              <div className="shb-mono shb-mono--clamp">
                                {inv.pix_copia_cola || "—"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="shb-invoice-card__foot">
                          <span className="muted">
                            Asaas Charge: <b>{inv.asaas_charge_id || "—"}</b>
                          </span>
                          <span className="muted">
                            Método:{" "}
                            <b>{(inv.payment_method || "undefined").toUpperCase()}</b>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── RULES ────────────────────────────────────────────── */}
            {tab === "rules" && cliente && (
              <div className="shb-pane">
                <div className="shb-pane__header">
                  <h3>Régua de cobrança &amp; Responsável</h3>
                  <button
                    className="shb-btn shb-btn--primary"
                    onClick={saveSettings}
                    disabled={savingSettings}
                  >
                    <FaSave /> {savingSettings ? "Salvando..." : "Salvar"}
                  </button>
                </div>

                <div className={`shb-automation-banner ${billingEnabled ? "is-on" : "is-off"}`}>
                  <div className="shb-automation-banner__text">
                    <strong>
                      {billingEnabled
                        ? "Cobrança automática ativa"
                        : "Cobrança automática pausada"}
                    </strong>
                    <p>
                      {billingEnabled
                        ? "A régua roda normalmente para este cliente: lembretes, cobrança vencida, suspensão e reativação automática."
                        : "Nada roda automaticamente para este cliente: sem lembrete, sem fatura nova, sem suspensão automática. Ações manuais aqui na tela continuam disponíveis normalmente."}
                    </p>
                  </div>
                  <label className={`shb-toggle ${togglingBilling ? "is-busy" : ""}`}>
                    <input
                      type="checkbox"
                      checked={billingEnabled}
                      disabled={togglingBilling}
                      onChange={(e) => toggleBillingEnabled(e.target.checked)}
                    />
                    <span className="shb-toggle__track">
                      <span className="shb-toggle__thumb" />
                    </span>
                  </label>
                </div>

                {/* ── Portão de início de cobrança (billing_start_date) ── */}
                <div className={`shb-automation-banner ${billingStarted ? "is-on" : "is-off"}`}>
                  <div className="shb-automation-banner__text">
                    <strong>
                      {billingStartDateIso
                        ? billingStarted
                          ? `Cobrança liberada desde ${billingStartDateIso}`
                          : `Cobrança bloqueada até ${billingStartDateIso}`
                        : "Início da cobrança não configurado"}
                    </strong>
                    <p>
                      {billingStartDateIso
                        ? billingStarted
                          ? "Faturas manuais e automáticas já podem ser geradas normalmente para este cliente."
                          : "Nenhuma fatura será gerada, manual ou automática, até essa data chegar."
                        : "Sem essa data, o sistema bloqueia qualquer geração de fatura para este cliente. Defina abaixo."}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="shb-btn shb-btn--ghost"
                    onClick={() => setEditBillingStartOpen((v) => !v)}
                  >
                    <FaCalendarAlt /> {editBillingStartOpen ? "Fechar" : "Ajustar"}
                  </button>
                </div>

                {editBillingStartOpen && (
                  <div className="shb-panel" style={{ marginBottom: "12px" }}>
                    <h4>Ajustar início da cobrança</h4>
                    <p className="muted" style={{ marginBottom: "10px" }}>
                      Use só para corrigir um erro na Central de Conversões ou mudar a
                      regra combinada com o cliente. O portão vale tanto para fatura
                      manual quanto para a automação.
                    </p>

                    <div className="shb-billingstart-options">
                      {BILLING_START_OPTIONS.map((opt) => {
                        const active = billingStartMode === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            className={`shb-billingstart-option ${active ? "is-active" : ""}`}
                            onClick={() => setBillingStartMode(opt.key)}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>

                    {billingStartMode === "custom" && (
                      <div className="shb-form" style={{ marginBottom: "10px" }}>
                        <label>
                          Data de início
                          <input
                            type="date"
                            value={billingStartCustomDate}
                            onChange={(e) => setBillingStartCustomDate(e.target.value)}
                          />
                        </label>
                      </div>
                    )}

                    <div className="shb-modal__actions" style={{ marginTop: 0 }}>
                      <button
                        type="button"
                        className="shb-btn shb-btn--ghost"
                        onClick={() => setEditBillingStartOpen(false)}
                        disabled={savingBillingStart}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="shb-btn shb-btn--primary"
                        onClick={saveBillingStart}
                        disabled={savingBillingStart}
                      >
                        <FaSave /> {savingBillingStart ? "Salvando..." : "Salvar"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="shb-grid2">
                  {/* ── Painel do responsável financeiro ── */}
                  <div className="shb-panel">
                    <h4>
                      <FaUserTie style={{ marginRight: "6px" }} />
                      Responsável Financeiro
                    </h4>
                    <p className="muted" style={{ marginBottom: "12px" }}>
                      Este contato receberá boletos, cobranças PIX, e-mails e SMS
                      gerados pela régua. Ele é definido em{" "}
                      <b>Clientes → Gerenciar → Visão Geral &amp; Contatos</b>.
                    </p>

                    {/* Card principal com dados do contato */}
                    <BillingContactCard contact={billingContact} />

                    {/* Canais de envio */}
                    <div className="shb-form__row" style={{ marginTop: "16px" }}>
                      <label className="shb-switch">
                        <input
                          type="checkbox"
                          checked={settings?.send_email ?? true}
                          onChange={(e) =>
                            updateSettingsField("send_email", e.target.checked)
                          }
                        />
                        <span>
                          <FaEnvelope style={{ marginRight: "4px" }} />
                          Email habilitado
                        </span>
                      </label>

                      <label className="shb-switch">
                        <input
                          type="checkbox"
                          checked={!!settings?.send_sms}
                          onChange={(e) =>
                            updateSettingsField("send_sms", e.target.checked)
                          }
                        />
                        <span>
                          <FaPhone style={{ marginRight: "4px" }} />
                          SMS habilitado
                        </span>
                      </label>
                    </div>

                    {/* Override avançado (usuário do sistema) */}
                    <details style={{ marginTop: "20px" }}>
                      <summary
                        style={{
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          color: "var(--shb-text-muted, #94a3b8)",
                          userSelect: "none",
                        }}
                      >
                        ⚙ Substituição manual por usuário do sistema (avançado)
                      </summary>
                      <div style={{ marginTop: "10px" }}>
                        <label
                          style={{
                            fontSize: "0.82rem",
                            color: "var(--shb-text-muted, #64748b)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          ID do usuário responsável (usuarios.id)
                          <input
                            className="shb-input"
                            value={settings?.responsavel_usuario_id || ""}
                            onChange={(e) =>
                              updateSettingsField(
                                "responsavel_usuario_id",
                                e.target.value
                              )
                            }
                            placeholder="Deixe vazio para usar o contato financeiro acima"
                          />
                        </label>
                        <p
                          style={{
                            fontSize: "0.75rem",
                            color: "#94a3b8",
                            marginTop: "6px",
                          }}
                        >
                          Preencher este campo sobrescreve o contato financeiro para
                          buscas no Asaas e notificações.
                        </p>
                      </div>
                    </details>
                  </div>

                  {/* ── Painel de configurações da régua ── */}
                  <div className="shb-panel">
                    <h4>Configurações da régua</h4>
                    <p className="muted">
                      Controle quantos dias antes avisar, recorrência de cobrança e
                      quando bloquear.
                    </p>

                    <div className="shb-form">
                      <div className="shb-form__row">
                        <label>
                          Dia do vencimento
                          <input
                            type="number"
                            min="1"
                            max="28"
                            value={settings?.due_day ?? 10}
                            onChange={(e) =>
                              updateSettingsField("due_day", Number(e.target.value))
                            }
                          />
                        </label>

                        <label>
                          Avisar antes (dias)
                          <input
                            type="number"
                            min="0"
                            max="30"
                            value={settings?.pre_due_days ?? 3}
                            onChange={(e) =>
                              updateSettingsField(
                                "pre_due_days",
                                Number(e.target.value)
                              )
                            }
                          />
                        </label>
                      </div>

                      <div className="shb-form__row">
                        <label>
                          Repetir aviso (dias)
                          <input
                            type="number"
                            min="1"
                            max="30"
                            value={settings?.notify_every_days ?? 2}
                            onChange={(e) =>
                              updateSettingsField(
                                "notify_every_days",
                                Number(e.target.value)
                              )
                            }
                          />
                        </label>

                        <label>
                          Bloquear após (dias)
                          <input
                            type="number"
                            min="1"
                            max="60"
                            value={settings?.block_after_days ?? 7}
                            onChange={(e) =>
                              updateSettingsField(
                                "block_after_days",
                                Number(e.target.value)
                              )
                            }
                          />
                        </label>
                      </div>

                      <div className="shb-form__row">
  <label className="shb-switch">
    <input
      type="checkbox"
      checked={settings?.auto_suspend ?? true}
      onChange={(e) =>
        updateSettingsField("auto_suspend", e.target.checked)
      }
    />
    <span>Auto-suspender</span>
  </label>

  <label className="shb-switch">
    <input
      type="checkbox"
      checked={settings?.auto_unsuspend ?? true}
      onChange={(e) =>
        updateSettingsField(
          "auto_unsuspend",
          e.target.checked
        )
      }
    />
    <span>Auto-reativar ao pagar</span>
  </label>
</div>

<div className="shb-form__row">
  <label className="shb-switch">
    <input
      type="checkbox"
      checked={settings?.notify_after_suspend ?? true}
      onChange={(e) =>
        updateSettingsField(
          "notify_after_suspend",
          e.target.checked
        )
      }
    />
    <span>Reforçar cobrança após suspensão</span>
  </label>
</div>
<p
  style={{
    fontSize: "0.78rem",
    color: "var(--shb-text-muted, #94a3b8)",
    margin: "-6px 0 4px 2px",
  }}
>
  Desligado: o cliente suspenso para de receber o lembrete de "vencida" a cada{" "}
  {settings?.notify_every_days ?? 2} dia(s). A suspensão e a reativação automática
  ao pagar continuam funcionando normalmente.
</p>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          padding: "10px 14px",
                          background: "#f0fdf4",
                          border: "1px solid #bbf7d0",
                          borderRadius: "8px",
                          fontSize: "0.83rem",
                          color: "#15803d",
                          marginTop: "4px",
                        }}
                      >
                        <FaCheckCircle style={{ flexShrink: 0, marginTop: "2px" }} />
                        <div>
                          <b>Automação ativa</b>
                          <p style={{ margin: "2px 0 0 0", color: "#166534" }}>
                            Um job automático já processa esta régua diariamente — pré-vencimento,
                            cobranças vencidas, suspensão e reativação rodam sem intervenção manual.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="shb-panel shb-panel--wide">
                  <h4>Mensagem padrão (suspensão)</h4>
                  <p className="shb-quote">
                    Sistema suspenso temporariamente. Procure a Gestão da
                    Conta/Suporte
                  </p>
                </div>
              </div>
            )}

            {/* ── HISTORY ──────────────────────────────────────────── */}
            {tab === "history" && (
              <div className="shb-pane">
                <div className="shb-pane__header">
                  <h3>Histórico (eventos)</h3>
                  <span className="muted">
                    Linha do tempo do tenant: cobrança, envio, pagamento, suspensão,
                    reativação.
                  </span>
                </div>

                {events.length === 0 ? (
                  <div className="shb-empty shb-empty--small">
                    <FaClock size={28} />
                    <h4>Nenhum evento registrado</h4>
                    <p>
                      Quando ações acontecerem (criar fatura, gerar cobrança, notificar,
                      webhook), aparecerão aqui.
                    </p>
                  </div>
                ) : (
                  <div className="shb-timeline">
                    {events.map((ev) => (
                      <div className="shb-timeline__item" key={ev.id}>
                        <div className="shb-timeline__dot" />
                        <div className="shb-timeline__content">
                          <div className="shb-timeline__top">
                            <b>{ev.event_type}</b>
                            <span className="muted">{isoDate(ev.created_at)}</span>
                          </div>
                          <div className="shb-timeline__msg">{ev.message}</div>
                          {ev.invoice_id ? (
                            <div className="shb-timeline__meta">
                              <Badge variant="brand" title="Vinculado à fatura">
                                Invoice #{ev.invoice_id}
                              </Badge>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Drawer>

      {/* MODAL: Create Invoice */}
      <Modal
        open={createInvoiceOpen}
        onClose={() => setCreateInvoiceOpen(false)}
        title="Criar nova fatura"
      >
        <div className="shb-form">
          <div className="shb-form__row">
            <label>
              Competência (YYYY-MM)
              <input
                value={invoiceDraft.competencia}
                onChange={(e) =>
                  setInvoiceDraft((d) => ({ ...d, competencia: e.target.value }))
                }
                placeholder="2026-04"
              />
            </label>

            <label>
              Vencimento
              <input
                type="date"
                value={invoiceDraft.due_date}
                onChange={(e) =>
                  setInvoiceDraft((d) => ({ ...d, due_date: e.target.value }))
                }
              />
            </label>
          </div>

          <label>
            Valor (R$)
            <input
              value={invoiceDraft.amountBRL}
              onChange={(e) =>
                setInvoiceDraft((d) => ({ ...d, amountBRL: e.target.value }))
              }
              placeholder="199,90"
            />
          </label>

          <div className="shb-modal__actions">
            <button
              className="shb-btn shb-btn--ghost"
              onClick={() => setCreateInvoiceOpen(false)}
            >
              Cancelar
            </button>
            <button className="shb-btn shb-btn--primary" onClick={createInvoice}>
              <FaFileInvoiceDollar /> Criar fatura
            </button>
          </div>

          <div className="shb-info">
            <FaBolt />
            <div>
              <b>Dica</b>
              <p>
                Depois de criar: gere a cobrança (boleto/pix) e envie a comunicação na
                sequência.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {/* MODAL: Notify */}
      <Modal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        title="Enviar comunicação"
      >
        <div className="shb-form">
          {/* Resumo do destinatário */}
          {billingContact && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 14px",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "8px",
                marginBottom: "12px",
                fontSize: "0.85rem",
              }}
            >
              <FaUserTie style={{ color: "#16a34a", flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 600, color: "#15803d" }}>
                  {billingContact.nome}
                </div>
                <div style={{ color: "#64748b" }}>
                  {billingContact.email} • {billingContact.telefone}
                </div>
              </div>
            </div>
          )}

          <label>
            Tipo de mensagem
            <select
              value={notifyDraft.type}
              onChange={(e) =>
                setNotifyDraft((d) => ({ ...d, type: e.target.value }))
              }
            >
              <option value="pre_due">Lembrete (pré-vencimento)</option>
              <option value="overdue">Vencida (inadimplência)</option>
              <option value="suspended">Suspensão</option>
              <option value="paid">Confirmação (pago)</option>
            </select>
          </label>

          <div className="shb-form__row">
            <label className="shb-switch">
              <input
                type="checkbox"
                checked={notifyDraft.channels.includes("email")}
                onChange={() => toggleChannel("email")}
              />
              <span>Email (Brevo)</span>
            </label>

            <label className="shb-switch">
              <input
                type="checkbox"
                checked={notifyDraft.channels.includes("sms")}
                onChange={() => toggleChannel("sms")}
              />
              <span>SMS (Comtele)</span>
            </label>
          </div>

          <div className="shb-modal__actions">
            <button
              className="shb-btn shb-btn--ghost"
              onClick={() => setNotifyOpen(false)}
            >
              Cancelar
            </button>
            <button className="shb-btn shb-btn--primary" onClick={sendNotify}>
              <FaPaperPlane /> Enviar agora
            </button>
          </div>

          <div className="shb-info">
            <FaCog />
            <div>
              <b>Nota</b>
              <p>
                Os templates vêm do banco (billing_templates). Se não houver template
                ativo, o backend usa fallback padrão.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {/* MONITOR: Faturas, comunicações e webhooks */}
      {monitorOpen && (
        <BillingMonitorModal
          onClose={() => setMonitorOpen(false)}
          initialClienteId={drawerOpen && cliente?.id ? cliente.id : ''}
          initialClienteNome={drawerOpen && cliente?.nome_empresa ? cliente.nome_empresa : ''}
        />
      )}
    </div>
  );
}